import { motion } from 'framer-motion';
import { ShieldCheck, Sparkle, UsersRound } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { AnimatedSection } from './AnimatedSection';
import { usePublicConfig } from '../context';

const stats = [
  { label: 'Members', value: '42' },
  { label: 'Cycle contribution', value: 'KES 210K' },
  { label: 'Payout success', value: '100%' },
];

export const Hero = () => {
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';

  return (
  <AnimatedSection className="bg-transparent" delay={0.05}>
    <div className="section-padding mx-auto flex w-full max-w-7xl flex-col items-center gap-12 text-center md:px-8 lg:flex-row lg:items-start lg:text-left">
      <div className="flex flex-1 flex-col items-center gap-6 lg:items-start">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700 dark:border-brand-300/20 dark:bg-brand-300/10 dark:text-brand-200">
          <Sparkle className="h-4 w-4" />
          Live Demo
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="font-display text-4xl leading-tight text-slate-900 sm:text-5xl lg:text-[3.5rem] lg:leading-[1.08] dark:text-white"
        >
          Stop using WhatsApp & notebooks for your chama
        </motion.h1>
        <p className="max-w-2xl text-base text-slate-600 sm:text-lg dark:text-slate-300">
          See how {chamaName} manages contributions, payouts, and members effortlessly. This is what YOUR chama could look like—professional, transparent, and stress-free.
        </p>
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
          <a
            href="tel:+254743269238"
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-brand-600 px-8 py-3 text-base font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-brand-700 sm:w-auto"
          >
            Call us: 0743 269 238
          </a>
          <a
            href="/apply"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 px-8 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-brand-500 hover:text-brand-600 dark:border-slate-700 dark:text-slate-200 dark:hover:border-brand-300 dark:hover:text-brand-200 sm:w-auto"
          >
            Explore the demo
          </a>
        </div>
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left dark:border-amber-900/30 dark:bg-amber-900/10">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            💡 This is a working demo of {chamaName}. Want this system for YOUR chama?
          </p>
          <div className="mt-3 flex flex-col gap-2 text-sm text-amber-800 dark:text-amber-300 sm:flex-row sm:items-center sm:gap-4">
            <a href="tel:+254743269238" className="font-semibold hover:underline">
              📞 0743 269 238
            </a>
            <a href="mailto:info@gritagencies.co.ke" className="font-semibold hover:underline">
              ✉️ info@gritagencies.co.ke
            </a>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, ease: [0.23, 1, 0.32, 1], delay: 0.2 }}
        className="flex-1 rounded-3xl border border-slate-200 bg-white p-8 shadow-md dark:border-slate-800 dark:bg-slate-950"
      >
        <div className="flex h-full flex-col justify-between gap-6">
          <div className="flex flex-col gap-6">
            <div className="inline-flex items-center gap-3 rounded-2xl border border-brand-500/40 bg-brand-50 px-4 py-3 text-sm font-semibold text-brand-700 dark:border-brand-300/40 dark:bg-slate-800 dark:text-brand-200">
              <ShieldCheck className="h-5 w-5" />
              Verified member activity
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Next payout window
                </h3>
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Scheduled
                </p>
                <p className="text-2xl font-semibold text-brand-600 dark:text-brand-300">
                  18 January
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Contribution status
                </h3>
                <p className="mt-2 flex items-center gap-2 text-sm text-brand-700 dark:text-brand-200">
                  <UsersRound className="h-4 w-4" />
                  41 / 42 already paid
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              "No more Excel chaos or missed WhatsApp messages. {chamaName} runs on autopilot—contributions tracked, payouts scheduled, everyone informed. This should be YOUR chama."
            </p>
            <div className="mt-4 flex items-center justify-between text-sm font-semibold text-slate-900 dark:text-slate-100">
              <span>Sample Chama Experience</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </AnimatedSection>
  );
};
